import * as turf from '@turf/turf'
import { debounce } from '../../../utils/Geotile'

/**
 * @typedef EdgeData
 * @property { Array } coordsInShape in a rectangular geojson feature's geometry, 
 *           the indexes of the points defining the ends of the edge
 * @property { Number } movingCoord which of lng (=0) or lat (=1) changes when moving this
 *           edge give a rectangle rotated 0 deg from N 
 */
const staticEdgeData = {
  top : { coordsInShape : [ 0, 1 ], movingCoord : 1 },
  right : { coordsInShape : [ 1, 2 ], movingCoord : 0 },
  left : { coordsInShape : [ 3, 0 ], movingCoord : 0 },
  bot : { coordsInShape : [ 2, 3 ], movingCoord : 1 }
}

/**
 * Gets coordinates of feature vertices
 * @param {Object} feature geojson feature
 * @returns {Array<Number[]>} coordinates : [ [ lng1, lat1 ], [ lng2, lat2 ], ... ]
 */
const getFeatureCoordinates = feature => feature?.geometry?.coordinates?.[0]

/**
 * @param {Object} feature geojson rectangle
 * @param {String} side 'top', 'bot', 'left', 'right'
 * @returns {Object} linestring
 */
function getSideLineString( feature, side ) {
  const coordinates = getFeatureCoordinates( feature )
  const { coordsInShape } = staticEdgeData[side]
  const bounds = [ coordinates[coordsInShape[0]], coordinates[coordsInShape[1]] ]
  return turf.lineString( bounds )
}

/**
 * Get line strings with names for each side of rectangle
 * @param {Object} feature geojson rectangle
 * @returns {Object[]} { name, line }
 */
function getAllFeatureSides( feature ) {
  return [ 'top', 'right', 'left', 'bot' ].map( name => ( { 
    name, 
    line : getSideLineString( feature, name ) 
  } ) )
}

/**
 * finds the edge of a geoshape nearest the clicked point
 * @param {Object} feature geojson shape
 * @param {Object} point turf point
 * @returns {Object} { name : <name of side>, 
 *                     dist : <min dist in meters from point to edge>, 
 *                     line : <line string for edge> }
 */
function findNearestEdge( feature, point ) {
  const edges = getAllFeatureSides( feature )
  let nearestEdge
  for ( let i = 0; i < edges.length; i++ ) {
    const curEdge = edges[i]
    const { name, line } = curEdge
    const dist = turf.pointToLineDistance( point, line, { units : 'meters' } )
    if ( !nearestEdge || nearestEdge.dist > dist ) {
      nearestEdge = { name, dist, line }
    }
  }
  return nearestEdge
}

/**
 * Gets the bearing of the midpoint of the top of a rectangle
 * Since this point will have a bearing of 0 when shape is not rotated
 * it is used to calculate rotation of all vertices in shape
 * @param {Object} feature geojson shape
 */
function getTopCenterBearing( feature ) {
  const top = getSideLineString( feature, 'top' )
  const featureCenter = turf.center( feature )
  const opts = { units : 'meters' }
  const lengthOfTop = turf.length( top, opts )
  const topCenter = turf.along( top, lengthOfTop / 2, opts )
  return turf.bearing( featureCenter, topCenter )
}

/**
 * For a given geoshape, returns coordinates of shape rotated to a bearing of 0
 * @param {Object} feature geojson rectangle
 * @param {Array?} coordinates optional coordinates to rotate (usually the mouse coordinates)
 *                             if omitted, will rotate the coordinates of the given feature
 * @returns {Array} coordinates of feature 
 */
function rotateToVertical( feature, coordinates ) {
  const center = turf.center( feature )
  let changeInBearing = getTopCenterBearing( feature )
  if ( Math.abs( changeInBearing ) < 0.1 ) {
    changeInBearing = 0
  }

  const coordinatesToRotate = coordinates || getFeatureCoordinates( feature )
  return coordinatesToRotate.map( ( coords ) => {
    const curBearing = turf.bearing( center, coords )
    const distanceFromCenter = turf.distance( center, coords )
    const newPoint = turf.destination( center, distanceFromCenter, curBearing - changeInBearing )
    return newPoint.geometry.coordinates
  } )
}

/**
 * Given a feature and a set of coordinates, rotates the coordinates
 * to match the bearing of the feature. The reverse of rotateToVertical
 * @param {Object} feature geojson feature with desired bearing
 * @param {Array} coordinatesToRotate the coordinates to rotate to the given shape's bearing
 * @returns {Array} coordinates defining shape
 */
function rotateFromVertical( feature, coordinatesToRotate ) {
  const featureCenter = turf.center( feature )
  const featureBearing = getTopCenterBearing( feature )
  return coordinatesToRotate.map( ( verticalCoords ) => {
    const curBearing = turf.bearing( featureCenter, verticalCoords )
    const distanceFromCenter = turf.distance( featureCenter, verticalCoords )
    const newPoint = turf.destination( featureCenter, distanceFromCenter, curBearing + featureBearing )
    return newPoint.geometry.coordinates
  } )
}

/**
 * Calculates the new coordinates of a given feature with a given edge moved to the distance of given coords
 * @param {Object} feature geojson rectangle
 * @param {String} edgeToMove 'top', 'bot', 'left', 'right' - which side are we moving?
 * @param {Array} _draggedCoords The coords that the given edge should be moved to meet
 * @returns {Array} coords defining shape with moved edge
 */
function moveEdge( feature, edgeToMove, _draggedCoords ) {
  // to simplify calcs with fixed lat or lng, rotate shape + dragged coords vertically
  const verticalCoords = rotateToVertical( feature ).slice( 0, 4 )
  const draggedCoords = rotateToVertical( feature, [ _draggedCoords ] )[0]

  // get indexes of the edge's vertices in the feature and which of lat/lng should change
  const { coordsInShape, movingCoord } = staticEdgeData[edgeToMove]
  const index0 = coordsInShape[0]
  const index1 = coordsInShape[1]

  // set the moving coord to equal that of the dragged point
  verticalCoords[index0][movingCoord] = draggedCoords[movingCoord]
  verticalCoords[index1][movingCoord] = draggedCoords[movingCoord]

  // complete the shape
  verticalCoords.push( verticalCoords[0] )
  return rotateFromVertical( feature, verticalCoords )
}

function init( opts ) {
  const { onDrawUpdate : _onDrawUpdate, getFeature } = opts
  const onDrawUpdate = debounce( _onDrawUpdate, 100 )

  return {
    onSetup( opts ) {
      this.map.dragPan.disable()
      return { selectedEdge : '' }
    },

    onMouseDown( state, e ) {
      const feature = getFeature()
      const { lngLat } = e
      const draggedPoint = turf.point( [ lngLat.lng, lngLat.lat ] )
      const nearestEdge = findNearestEdge( feature, draggedPoint )
      if ( nearestEdge && nearestEdge.dist < 15 ) {
        state.selectedEdge = nearestEdge.name
      } else {
        state.selectedEdge = ''
      }
    },

    onDrag( state, e ) {
      const { selectedEdge } = state
      if ( selectedEdge ) {
        const { lngLat : { lng, lat } } = e
        const draggedCoords = [ lng, lat ]
        const originalFeature = getFeature()
        const nCoords = moveEdge( originalFeature, selectedEdge, draggedCoords )
        const np = { 
          ...originalFeature,
          geometry : { 
            ...originalFeature.geometry, 
            coordinates : [ nCoords ]
          }
        }
        
        this._ctx.api.add( np )
        onDrawUpdate( np )
      }
    },

    onStop() {
      this.map.dragPan.enable()
    },

    toDisplayFeatures( state, geojson, display ) {
      return display( geojson );
    }

  }
}

exports.init = init
